import type { CSSProperties } from "react";
import { useSwarmData } from "../hooks/useSwarmData";
import { AgentCard } from "../components/AgentCard";
import { ErrorBanner } from "../components/ErrorBanner";
import { AgentControlPanel, COMMAND_CATALOG } from "../components/AgentControlPanel";

const AUTHORITY_LEVELS = [
  {
    level: 1,
    title: "Autonomous",
    desc: "No approval needed. Acts immediately.",
    color: "var(--color-status-active)",
    border: "rgba(132, 204, 22, 0.35)",
    bg: "rgba(132, 204, 22, 0.06)",
  },
  {
    level: 2,
    title: "Notify",
    desc: "Acts and notifies CEO Agent.",
    color: "var(--color-status-running)",
    border: "rgba(56, 189, 248, 0.35)",
    bg: "rgba(56, 189, 248, 0.06)",
  },
  {
    level: 3,
    title: "Approval",
    desc: "Waits for CEO approval before acting.",
    color: "var(--color-accent-bright)",
    border: "rgba(245, 158, 11, 0.4)",
    bg: "rgba(245, 158, 11, 0.07)",
  },
  {
    level: 4,
    title: "Human",
    desc: "Waits for MrPix (human) approval.",
    color: "var(--color-status-error)",
    border: "rgba(239, 68, 68, 0.35)",
    bg: "rgba(239, 68, 68, 0.06)",
  },
];

export function Agents() {
  const { data, loading, error, refresh } = useSwarmData();

  if (error && !data) {
    return <ErrorBanner message={error} onRetry={refresh} context="agent roster" />;
  }

  if (loading || !data) {
    return (
      <div className="flex items-center gap-3 text-[var(--color-text-dim)]">
        <span className="live-dot text-[var(--color-accent)]" />
        <span className="font-mono text-xs tracking-[0.18em] uppercase blink">
          Loading agent roster…
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-10">
      <header className="reveal">
        <p className="eyebrow">Console · 02</p>
        <h2 className="font-display italic text-[56px] leading-[0.95] mt-2 tracking-tight">
          <span className="text-[var(--color-text)]">The </span>
          <span className="text-accent-gradient">Roster</span>
        </h2>
        <p className="mt-3 text-sm text-[var(--color-text-muted)] max-w-2xl">
          Every agent in the swarm — its current state, authority bracket, and operational footprint.
        </p>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {data.agents.map((agent, i) => (
          <div
            key={agent.id}
            className="reveal"
            style={{ animationDelay: `${80 + i * 60}ms` } as CSSProperties}
          >
            <AgentCard {...agent} />
          </div>
        ))}
      </section>

      {/* Realtime command panels — Phase 13 U4 #296 */}
      <section className="space-y-4 reveal" style={{ animationDelay: "450ms" }}>
        <div className="section-rule">
          <p className="eyebrow">// Realtime commands</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {COMMAND_CATALOG.map((panel) => (
            <AgentControlPanel
              key={panel.agentSlug}
              agentSlug={panel.agentSlug}
              title={panel.title}
              commands={panel.commands}
            />
          ))}
        </div>
      </section>

      {/* Authority reference */}
      <section className="space-y-4">
        <div className="section-rule">
          <p className="eyebrow">// Authority Ladder</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {AUTHORITY_LEVELS.map((lvl, i) => (
            <div
              key={lvl.level}
              className="panel panel-hover p-5 reveal relative overflow-hidden"
              style={{
                background: `linear-gradient(180deg, ${lvl.bg}, var(--color-bg-elev) 80%)`,
                borderColor: lvl.border,
                animationDelay: `${500 + i * 70}ms`,
              } as CSSProperties}
            >
              <span
                className="absolute top-0 left-0 w-full h-px"
                style={{ background: lvl.color, opacity: 0.6 }}
              />
              <div className="flex items-baseline gap-3 mb-3">
                <span
                  className="font-display italic text-4xl leading-none"
                  style={{ color: lvl.color }}
                >
                  L{lvl.level}
                </span>
                <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--color-text-muted)]">
                  Level {lvl.level}
                </span>
              </div>
              <p
                className="font-display italic text-xl leading-tight"
                style={{ color: lvl.color }}
              >
                {lvl.title}
              </p>
              <p className="text-xs mt-2 text-[var(--color-text-muted)] leading-relaxed">
                {lvl.desc}
              </p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
